import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "wouter";
import {
  addPendingStill,
  clearRecordingSession,
  getRecordingSession,
  listPendingStills,
  listRecordingSessions,
  loadRecoveredRecording,
  removePendingStill,
  type RecordingSessionMeta,
} from "@/lib/packing-recording-store";
import {
  getRecordingMirror,
  type RecordingMirror,
  type CompletedUpload,
} from "@/lib/packing-recording-mirror";
import {
  createVideoRecorder,
  getUserMediaWithFallback,
  pickVideoMimeType,
} from "@/lib/video-capture";
import { customFetch } from "@workspace/api-client-react";

/**
 * App-wide persistent packing recorder.
 *
 * The MediaRecorder + getUserMedia stream live here, at the App root, so the
 * recording survives in-app navigation (route/tab switches). The webcam
 * capture modal stops on unmount; this provider deliberately does NOT, which
 * is what lets the operator press "Start packing", walk around the app, and
 * still have a single continuous clip waiting when they hit "Packing
 * complete".
 *
 * A full browser refresh/close kills the live MediaStream (it can't be
 * serialised), so we keep the `beforeunload` warning while recording. But the
 * captured bytes are no longer lost: every chunk is mirrored into IndexedDB
 * (see `packing-recording-store`) keyed to the job, so after a refresh/close/
 * crash the clip can be reassembled and attached. The persisted copy is
 * cleared only once the clip has been successfully uploaded.
 */

function formatRecTime(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${String(rem).padStart(2, "0")}`;
}

export interface StartRecordingOpts {
  /** The packing job this recording belongs to. */
  jobId: string;
  /** Capture audio as well as video (tenant setting). */
  audio: boolean;
  /**
   * When true the operator cannot stop the recording manually — it only ends
   * via `stopAndCollect()` (i.e. "Packing complete"). Surfaced on the floating
   * indicator so the operator understands why there's no stop button.
   */
  locked: boolean;
  /** Optional file-name prefix; a timestamp is appended. */
  fileNamePrefix?: string;
}

export type StartResult = { ok: true } | { ok: false; error: string };

interface PackingRecorderContextValue {
  /** Whether a recording is currently in flight. */
  isRecording: boolean;
  /** Elapsed seconds of the current recording. */
  elapsed: number;
  /** The job id the current recording belongs to (null when idle). */
  activeJobId: string | null;
  /** Whether the current recording is locked (no manual stop). */
  locked: boolean;
  /** Begin recording. Resolves once the stream + recorder are live. */
  start: (opts: StartRecordingOpts) => Promise<StartResult>;
  /**
   * Stop the recording and resolve with the captured clip as a single-element
   * `File[]` (empty array if nothing was captured). Tears down the stream.
   *
   * The durable IndexedDB copy is intentionally NOT cleared here — it is kept
   * until the caller confirms a successful upload via `clearPersisted(jobId)`,
   * so a failed upload still leaves a recoverable artifact.
   */
  stopAndCollect: () => Promise<File[]>;
  /** Abandon the recording without producing a file. Tears down the stream. */
  cancel: () => void;
  /**
   * Whether a durable (IndexedDB-persisted) recording exists for a job that is
   * NOT currently being recorded in-memory — i.e. a clip left behind by an
   * interrupted session (refresh/close/crash) that can be recovered.
   */
  hasRecoverable: (jobId: string) => Promise<boolean>;
  /**
   * Reassemble a persisted/interrupted recording into a File ready to upload,
   * or null when nothing is recoverable.
   */
  recoverFile: (jobId: string) => Promise<File | null>;
  /** Clear the durable copy for a job once it has been uploaded (or discarded). */
  clearPersisted: (jobId: string) => Promise<void>;
  /**
   * List every durable recording session that is NOT the one currently being
   * recorded in-memory — i.e. clips left behind by interrupted sessions across
   * ALL jobs. Drives the background auto-flush.
   */
  listRecoverableSessions: () => Promise<RecordingSessionMeta[]>;
  /**
   * Live progress of the in-flight progressive upload (Task #1252), or null
   * when there is no progressive upload (fallback mode / not yet started).
   * `uploadedBytes`/`recordedBytes` let the UI show an upload bar that keeps up
   * with the recording so "Packing complete" rarely waits on a big upload.
   */
  uploadProgress: { recordedBytes: number; uploadedBytes: number } | null;
  /**
   * Finalise the progressive upload for a job and resolve with the completed
   * object's details so the caller can attach it WITHOUT re-uploading, or null
   * when progressive upload didn't complete (so the caller falls back to the
   * existing full upload of the `File[]` from `stopAndCollect`). MUST be called
   * only AFTER `stopAndCollect()` has resolved (so every chunk has been mirrored
   * to the worker). Always best-effort.
   */
  getCompletedUpload: (jobId: string) => Promise<CompletedUpload | null>;
}

const PackingRecorderContext = createContext<PackingRecorderContextValue | null>(
  null,
);

export function usePackingRecorder(): PackingRecorderContextValue {
  const ctx = useContext(PackingRecorderContext);
  if (!ctx) {
    throw new Error(
      "usePackingRecorder must be used within a <PackingRecorderProvider>",
    );
  }
  return ctx;
}

export function PackingRecorderProvider({ children }: { children: ReactNode }) {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<
    { recordedBytes: number; uploadedBytes: number } | null
  >(null);
  // Unsubscribe handle for the current recording's upload-progress listener.
  const progressUnsubRef = useRef<(() => void) | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prefixRef = useRef<string>("packing-proof");
  // The job id whose chunks are currently being mirrored to IndexedDB. Held in
  // a ref (not just state) so the `ondataavailable` closure always persists to
  // the right job even before React state settles.
  const persistJobIdRef = useRef<string | null>(null);
  // Off-thread mirror for the durable write path (begin/append/clear). Spawns a
  // Web Worker when available, else falls back to direct main-thread store
  // calls. Held in a ref so the same instance is reused by `ondataavailable`
  // and `cancel`.
  const mirrorRef = useRef<RecordingMirror | null>(null);

  const stillTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Mirrors `isRecording` into a ref so the still-capture interval can gate
  // itself synchronously without a stale closure over state.
  const isRecordingRef = useRef(false);

  const teardownStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (stillTimerRef.current) {
      clearInterval(stillTimerRef.current);
      stillTimerRef.current = null;
    }
  }, []);

  const start = useCallback(
    async (opts: StartRecordingOpts): Promise<StartResult> => {
      // Guard: never run two recordings at once. The job page only calls this
      // on a fresh "Start packing", but a defensive check avoids leaking a
      // second stream if state races.
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        return { ok: false, error: "A recording is already in progress." };
      }
      if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
        return { ok: false, error: "This browser does not support camera access." };
      }
      if (typeof MediaRecorder === "undefined") {
        return { ok: false, error: "This browser does not support video recording." };
      }
      prefixRef.current = opts.fileNamePrefix ?? "packing-proof";
      let stream: MediaStream;
      try {
        stream = await getUserMediaWithFallback({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: opts.audio,
        });
      } catch (err) {
        const e = err as DOMException | Error;
        const name = (e as DOMException).name ?? "";
        if (name === "NotAllowedError" || name === "PermissionDeniedError") {
          return {
            ok: false,
            error:
              "Camera access was denied. Allow camera permissions in your browser settings, then try again.",
          };
        }
        if (name === "NotFoundError" || name === "DevicesNotFoundError") {
          return { ok: false, error: "No camera was found on this device." };
        }
        if (name === "NotReadableError") {
          return { ok: false, error: "The camera is in use by another application." };
        }
        if (name === "OverconstrainedError" || name === "ConstraintNotSatisfiedError") {
          return {
            ok: false,
            error: "This camera can't provide a compatible video format. Try a different camera.",
          };
        }
        return { ok: false, error: e?.message ?? "Could not start the camera." };
      }

      const mimeType = pickVideoMimeType();
      const mr = createVideoRecorder(stream, mimeType);
      if (!mr) {
        stream.getTracks().forEach((t) => t.stop());
        return { ok: false, error: "This browser does not support video recording." };
      }
      // The browser may settle on a different container than requested (Safari
      // → mp4 even when we passed nothing); record the actual type so a
      // recovered clip is reassembled + named with the correct extension.
      const effectiveMimeType = mr.mimeType || mimeType || "video/webm";

      streamRef.current = stream;
      recorderRef.current = mr;
      chunksRef.current = [];
      persistJobIdRef.current = opts.jobId;
      // Begin a fresh durable session (clears any stale persisted chunks for
      // this job). Best-effort: if IndexedDB is unavailable the recorder still
      // works, it just loses crash recovery. The mirror does the IndexedDB
      // writes off the main thread (Web Worker) when available.
      const mirror = getRecordingMirror();
      mirrorRef.current = mirror;
      mirror.begin({
        jobId: opts.jobId,
        mimeType: effectiveMimeType,
        prefix: prefixRef.current,
        startedAt: Date.now(),
      });
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
          // Mirror every chunk to IndexedDB so an interrupted session leaves a
          // recoverable artifact on disk.
          const pj = persistJobIdRef.current;
          if (pj) mirror.append(pj, e.data);
        }
      };
      mr.start(250);

      // Progressive upload (Task #1252) — worker mode only. We mint a GCS
      // resumable session on the main thread (so the Clerk token is attached)
      // and hand the capability URL to the worker, which PUTs the bytes as they
      // accrue. This is purely an optimisation so "Packing complete" doesn't
      // block on a big upload; ANY failure here silently falls back to the
      // existing full-upload-at-stop path. Reset/clean prior progress state.
      progressUnsubRef.current?.();
      progressUnsubRef.current = null;
      setUploadProgress(null);
      if (mirror.supportsUpload) {
        const uploadJobId = opts.jobId;
        progressUnsubRef.current = mirror.onProgress((jid, p) => {
          if (jid === uploadJobId) setUploadProgress(p);
        });
        const ext = effectiveMimeType.includes("mp4") ? "mp4" : "webm";
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const fileName = `${prefixRef.current}-${ts}.${ext}`;
        // Fire-and-forget mint; do NOT await (recording is already live).
        void (async () => {
          try {
            const data = await customFetch<{
              sessionUrl: string;
              objectPath: string;
            }>(
              `${import.meta.env.BASE_URL}api/storage/uploads/request-resumable-attachment-url`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ contentType: effectiveMimeType }),
              },
            );
            // Only begin if this is still the active recording for the job (the
            // operator might have cancelled before the mint returned).
            if (
              persistJobIdRef.current === uploadJobId &&
              data?.sessionUrl &&
              data?.objectPath
            ) {
              mirror.beginUpload({
                jobId: uploadJobId,
                sessionUrl: data.sessionUrl,
                objectPath: data.objectPath,
                contentType: effectiveMimeType,
                fileName,
              });
            } else {
              mirror.abortUpload(uploadJobId);
            }
          } catch {
            // Mint failed — tell the worker to stop buffering for upload and
            // free its memory; the full-upload fallback still covers the clip.
            mirror.abortUpload(uploadJobId);
          }
        })();
      }

      setActiveJobId(opts.jobId);
      setLocked(opts.locked);
      isRecordingRef.current = true;
      setIsRecording(true);
      setElapsed(0);
      const startedAt = Date.now();
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAt) / 1000));
      }, 250);

      // Still captures — every 15 seconds snapshot a JPEG from the live stream
      // and attach it to the packing job as capturedVia="still". Best-effort:
      // any failure (mint, PUT, POST) is swallowed silently so the main
      // recording is never interrupted. The still timer is cleared in
      // teardownStream (called by stop/cancel/unmount) AND gated inside each
      // tick by isRecordingRef so it self-stops the moment the job leaves the
      // in_progress state even if teardownStream hasn't been called yet.
      const stillJobId = opts.jobId;
      stillTimerRef.current = setInterval(() => {
        void (async () => {
          // Self-stop guard: if recording ended (any path) or the job id
          // drifted, bail out and clear the interval immediately.
          if (!isRecordingRef.current || persistJobIdRef.current !== stillJobId) {
            if (stillTimerRef.current) {
              clearInterval(stillTimerRef.current);
              stillTimerRef.current = null;
            }
            return;
          }
          const stream = streamRef.current;
          if (!stream) return;
          try {
            // Capture a frame from the live video track via a temporary
            // canvas/video element. Works in Chrome/Safari/Firefox without
            // the ImageCapture API (which is not universally available).
            const videoTrack = stream.getVideoTracks()[0];
            if (!videoTrack) return;
            const settings = videoTrack.getSettings();
            const w = settings.width ?? 1280;
            const h = settings.height ?? 720;
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            if (!ctx) return;

            // Draw via a temporary hidden video element so we get the current frame.
            const video = document.createElement("video");
            video.muted = true;
            video.playsInline = true;
            video.srcObject = stream;
            await new Promise<void>((resolve, reject) => {
              video.onloadedmetadata = () => {
                video.play().then(() => resolve()).catch(reject);
              };
              video.onerror = reject;
              // Safety: bail if the stream disappears before the video loads.
              setTimeout(reject, 3000);
            });
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            video.pause();
            video.srcObject = null;

            const blob = await new Promise<Blob | null>((resolve) =>
              canvas.toBlob(resolve, "image/jpeg", 0.85),
            );
            if (!blob || blob.size === 0) return;

            // Mint a presigned URL for this still image.
            const ts = new Date().toISOString().replace(/[:.]/g, "-");
            const fileName = `still-${ts}.jpg`;
            const mintData = await customFetch<{
              uploadURL: string;
              objectPath: string;
            }>(
              `${import.meta.env.BASE_URL}api/storage/uploads/request-private-attachment-url`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  name: fileName,
                  size: blob.size,
                  contentType: "image/jpeg",
                }),
              },
            );
            if (!mintData?.uploadURL || !mintData?.objectPath) return;

            // Verify this is still the active recording for this job before uploading.
            if (persistJobIdRef.current !== stillJobId) return;

            // PUT the JPEG to GCS (raw fetch — no Bearer needed for presigned PUT).
            const putRes = await fetch(mintData.uploadURL, {
              method: "PUT",
              body: blob,
              headers: { "Content-Type": "image/jpeg" },
            });
            if (!putRes.ok) return;

            // Final check before attaching — another concurrent cancel could
            // have cleared persistJobIdRef between the PUT and the POST.
            if (persistJobIdRef.current !== stillJobId) return;

            // Write a WAL record AFTER the GCS PUT (object now exists in GCS)
            // but BEFORE the registration POST. If the tab closes between these
            // two steps the record is left in IndexedDB and drained on the next
            // session load, so the GCS object is registered rather than
            // silently orphaned.
            const pendingStillId = await addPendingStill({
              jobId: stillJobId,
              objectPath: mintData.objectPath,
              fileName,
              contentType: "image/jpeg",
              sizeBytes: blob.size,
              savedAt: Date.now(),
            });

            // Attach the still to the packing job.
            await customFetch(
              `${import.meta.env.BASE_URL}api/packing/jobs/${stillJobId}/media`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  objectPath: mintData.objectPath,
                  fileName,
                  contentType: "image/jpeg",
                  mediaType: "image",
                  sizeBytes: blob.size,
                  capturedVia: "still",
                }),
              },
            );

            // POST confirmed — remove the WAL record so the still is not
            // re-registered on recovery.
            if (pendingStillId !== null) {
              void removePendingStill(pendingStillId);
            }
          } catch {
            // Best-effort: still captures are non-critical. Never crash the
            // recording loop.
          }
        })();
      }, 15_000);

      return { ok: true };
    },
    [],
  );

  const stopAndCollect = useCallback((): Promise<File[]> => {
    const mr = recorderRef.current;
    if (!mr || mr.state === "inactive") {
      // Nothing recording — clean up defensively and resolve empty.
      teardownStream();
      recorderRef.current = null;
      isRecordingRef.current = false;
      setIsRecording(false);
      setActiveJobId(null);
      setLocked(false);
      setElapsed(0);
      return Promise.resolve([]);
    }
    return new Promise<File[]>((resolve) => {
      mr.onstop = () => {
        const type = mr.mimeType || "video/webm";
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        // Stop mirroring further chunks. The durable copy is deliberately NOT
        // cleared here — the caller clears it via `clearPersisted(jobId)` only
        // after a confirmed upload, so a failed upload stays recoverable.
        persistJobIdRef.current = null;
        teardownStream();
        recorderRef.current = null;
        isRecordingRef.current = false;
        setIsRecording(false);
        setActiveJobId(null);
        setLocked(false);
        setElapsed(0);
        if (blob.size === 0) {
          resolve([]);
          return;
        }
        const ext = type.includes("mp4") ? "mp4" : "webm";
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const file = new File([blob], `${prefixRef.current}-${ts}.${ext}`, {
          type,
        });
        resolve([file]);
      };
      try {
        mr.stop();
      } catch {
        // If stop throws, fall back to a clean teardown + empty result so the
        // caller's "complete" flow isn't wedged.
        persistJobIdRef.current = null;
        teardownStream();
        recorderRef.current = null;
        chunksRef.current = [];
        isRecordingRef.current = false;
        setIsRecording(false);
        setActiveJobId(null);
        setLocked(false);
        setElapsed(0);
        resolve([]);
      }
    });
  }, [teardownStream]);

  const cancel = useCallback(() => {
    const mr = recorderRef.current;
    if (mr && mr.state !== "inactive") {
      mr.onstop = null;
      try {
        mr.stop();
      } catch {
        /* noop */
      }
    }
    // Explicit abandon — discard the durable copy too. Routed through the
    // mirror so the clear is ordered AFTER any still-queued append messages for
    // this job (clearing directly could race ahead and leave orphan chunks).
    const pj = persistJobIdRef.current;
    if (pj) {
      const m = mirrorRef.current ?? getRecordingMirror();
      m.abortUpload(pj);
      m.clear(pj);
    }
    progressUnsubRef.current?.();
    progressUnsubRef.current = null;
    setUploadProgress(null);
    persistJobIdRef.current = null;
    chunksRef.current = [];
    teardownStream();
    recorderRef.current = null;
    isRecordingRef.current = false;
    setIsRecording(false);
    setActiveJobId(null);
    setLocked(false);
    setElapsed(0);
  }, [teardownStream]);

  // Recovery helpers — surfaced on the job page so an interrupted session
  // (refresh/close/crash) can be re-attached instead of silently lost.
  const hasRecoverable = useCallback(
    async (jobId: string): Promise<boolean> => {
      // A live recording for this job isn't "recoverable" — it's still going.
      if (persistJobIdRef.current === jobId) return false;
      const meta = await getRecordingSession(jobId);
      return meta !== null;
    },
    [],
  );

  const recoverFile = useCallback(
    (jobId: string): Promise<File | null> => loadRecoveredRecording(jobId),
    [],
  );

  const clearPersisted = useCallback(
    (jobId: string): Promise<void> => clearRecordingSession(jobId),
    [],
  );

  const getCompletedUpload = useCallback(
    async (jobId: string): Promise<CompletedUpload | null> => {
      const mirror = mirrorRef.current ?? getRecordingMirror();
      if (!mirror.supportsUpload) return null;
      try {
        const result = await mirror.finalizeUpload(jobId);
        return result;
      } catch {
        return null;
      } finally {
        // Progressive upload for this job is done one way or another — drop the
        // progress listener + bar so the next recording starts clean.
        progressUnsubRef.current?.();
        progressUnsubRef.current = null;
        setUploadProgress(null);
      }
    },
    [],
  );

  const listRecoverableSessions = useCallback(
    async (): Promise<RecordingSessionMeta[]> => {
      const all = await listRecordingSessions();
      // A live recording's session is on disk too, but it's still going — never
      // treat it as recoverable until it has been stopped + persisted whole.
      const activeJob = persistJobIdRef.current;
      return activeJob ? all.filter((s) => s.jobId !== activeJob) : all;
    },
    [],
  );

  // On mount: drain any pending-still records left behind by a prior crashed or
  // closed tab. Each record represents a still image that was successfully PUT
  // to GCS but whose registration POST never completed. We attempt the POST now
  // and remove the record on success; failures are left for the next load.
  // Best-effort: never crashes if IndexedDB or the network is unavailable.
  useEffect(() => {
    void (async () => {
      try {
        const pending = await listPendingStills();
        for (const record of pending) {
          try {
            await customFetch(
              `${import.meta.env.BASE_URL}api/packing/jobs/${record.jobId}/media`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  objectPath: record.objectPath,
                  fileName: record.fileName,
                  contentType: record.contentType,
                  mediaType: "image",
                  sizeBytes: record.sizeBytes,
                  capturedVia: "still",
                }),
              },
            );
            // Registration confirmed — clear the WAL record.
            if (record.id !== undefined) {
              void removePendingStill(record.id);
            }
          } catch {
            // Leave the record in place; it will be retried on the next load.
          }
        }
      } catch {
        // IndexedDB unavailable — nothing to drain.
      }
    })();
  }, []);

  // Warn on full browser refresh/close while a clip is in flight (it would be
  // lost — the stream can't be serialised). In-app navigation is safe.
  useEffect(() => {
    if (!isRecording) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isRecording]);

  // Stop all tracks if the provider itself ever unmounts (app teardown).
  useEffect(() => {
    return () => {
      const mr = recorderRef.current;
      if (mr && mr.state !== "inactive") {
        mr.onstop = null;
        try {
          mr.stop();
        } catch {
          /* noop */
        }
      }
      teardownStream();
    };
  }, [teardownStream]);

  const value = useMemo<PackingRecorderContextValue>(
    () => ({
      isRecording,
      elapsed,
      activeJobId,
      locked,
      start,
      stopAndCollect,
      cancel,
      hasRecoverable,
      recoverFile,
      clearPersisted,
      listRecoverableSessions,
      uploadProgress,
      getCompletedUpload,
    }),
    [
      isRecording,
      elapsed,
      activeJobId,
      locked,
      start,
      stopAndCollect,
      cancel,
      hasRecoverable,
      recoverFile,
      clearPersisted,
      listRecoverableSessions,
      uploadProgress,
      getCompletedUpload,
    ],
  );

  return (
    <PackingRecorderContext.Provider value={value}>
      {children}
      {isRecording && typeof document !== "undefined"
        ? createPortal(
            <div
              className="fixed bottom-4 right-4 z-[100] flex items-center gap-2 rounded-full bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg"
              data-testid="packing-rec-indicator"
            >
              <span className="h-2.5 w-2.5 rounded-full bg-white animate-pulse" />
              REC {formatRecTime(elapsed)}
              {activeJobId ? (
                <Link
                  href={`/packing/${activeJobId}`}
                  className="ml-1 underline decoration-white/60 underline-offset-2 hover:decoration-white"
                  data-testid="link-packing-rec-job"
                >
                  Job
                </Link>
              ) : null}
              {locked ? (
                <span className="ml-0.5 opacity-80">· locked</span>
              ) : null}
              {uploadProgress && uploadProgress.recordedBytes > 0 ? (
                <span
                  className="ml-0.5 opacity-80"
                  data-testid="text-packing-rec-upload"
                  title="Proof-of-pack video is uploading as you record"
                >
                  · ⬆{" "}
                  {Math.min(
                    100,
                    Math.round(
                      (uploadProgress.uploadedBytes /
                        Math.max(1, uploadProgress.recordedBytes)) *
                        100,
                    ),
                  )}
                  %
                </span>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </PackingRecorderContext.Provider>
  );
}
